import { useState, useEffect, useCallback, useMemo } from 'react';
import { fulfillmentApi } from '../lib/api';
import { readFileAsDataUrl, resizeToJpeg } from '../lib/images';
import AllSitesLink from '../components/AllSitesLink';
import StaffSignIn from '../components/StaffSignIn';
import useStaffSession from '../hooks/useStaffSession';

// Fulfillment — fulfillment.silverservice.com. "Did the order arrive?" for the
// office and drivers: what goes out when, where each order is (SOS's pick
// ticket, shipment and invoice, plus the driver's delivery record), and what
// needs chasing. Reads SOS; records deliveries. Sales reps look in on their
// own customers' orders (the rep filter starts on theirs) but can't record
// anything. See docs/site-map.md.
export default function Fulfillment() {
  const session = useStaffSession('fulfillment');
  const { token } = session;
  const [me, setMe] = useState(null);
  const [board, setBoard] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('');
  const [orderId, setOrderId] = useState(null);
  const [repFilter, setRepFilter] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    return Promise.all([fulfillmentApi.me(), fulfillmentApi.board()])
      .then(([m, b]) => { setMe(m); setBoard(b); setView((v) => v || (m.role === 'delivery' ? 'deliveries' : 'board')); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  if (!token) return <StaffSignIn session={session} siteName="Fulfillment" />;

  const reps = (board && board.reps) || [];
  const ownBook = me && me.role === 'sales_rep' ? me.email.toLowerCase() : 'all';
  const rep = repFilter || ownBook;
  const shown = forRep(board, rep);
  const orders = shown ? shown.orders : [];
  const problemCount = orders.filter((o) => o.problems.length).length + (shown ? shown.olderClosedNoInvoice.length : 0);
  const tabs = [['board', 'Board'], ['deliveries', 'Deliveries'], ['problems', `Problems${problemCount ? ` (${problemCount})` : ''}`], ['find', 'Find an order']];
  const back = () => { setOrderId(null); load(); };

  let body = null;
  if (orderId) body = <OrderDetail orderId={orderId} onBack={back} viewOnly={!!(me && me.viewOnly)} />;
  else if (shown && view === 'board') body = <Board board={shown} onOpen={setOrderId} />;
  else if (shown && view === 'deliveries') body = <Deliveries board={shown} onOpen={setOrderId} />;
  else if (shown && view === 'problems') body = <Problems board={shown} onOpen={setOrderId} />;
  else if (shown && view === 'find') body = <Find board={shown} onOpen={setOrderId} />;

  return (
    <div style={s.page}>
      <style>{PRINT_CSS}</style>
      <div style={s.topBar} className="no-print">
        <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          <img src="/logo.png" alt="Silver Service" style={s.logoImg} />
          <span style={s.brandSmSub}>Fulfillment</span>
        </div>
        <div style={s.topRight}>
          <span style={s.who}>{me?.name}</span>
          <AllSitesLink style={s.linkBtn} />
          <button style={s.linkBtn} onClick={session.signOut} title="Signs this browser out of every staff site">Sign out</button>
        </div>
      </div>
      <div style={s.tabs} className="no-print">
        {tabs.map(([key, label]) => (
          <button key={key} style={view === key && !orderId ? { ...s.tab, ...s.tabActive } : s.tab} onClick={() => { setOrderId(null); setView(key); }}>{label}</button>
        ))}
        <button style={s.refresh} onClick={load} disabled={loading} title="Read the latest from SOS">{loading ? 'Updating…' : '↻ Refresh'}</button>
      </div>
      <div style={s.body}>
        {error && <p style={s.err}>{error}</p>}
        {board && !orderId && (reps.length > 0 || rep !== 'all') && (
          <RepFilter value={rep} onChange={setRepFilter} reps={reps} me={me} />
        )}
        {!board && !error ? <p style={s.muted}>Reading orders from SOS…</p> : body}
      </div>
    </div>
  );
}

// ── Labels ──
const STAGE_LABELS = { ordered: 'Ordered', picking: 'Picking', shipped: 'Shipped', invoiced: 'Invoiced', delivered: 'Delivered' };
const STAGE_ORDER = ['ordered', 'picking', 'shipped', 'invoiced', 'delivered'];
const METHOD_META = {
  route: { label: 'Route', color: '#1a5fb4', bg: '#e8f0fb' },
  fedex: { label: 'FedEx', color: '#5b3cc4', bg: '#efeafd' },
  pickup: { label: 'Pickup', color: '#8a5a00', bg: '#fdf0d5' },
};
const PROBLEM_META = {
  late: { label: 'Late', help: 'Past its expected day and not shipped yet.' },
  backordered: { label: 'Backordered', help: 'Some lines are backordered in SOS.' },
  not_invoiced: { label: 'Shipped, not invoiced', help: 'Shipped more than three days ago and still not invoiced in SOS, so QuickBooks has not billed it.' },
  closed_no_invoice: { label: 'Closed without an invoice', help: 'Closed in SOS but never invoiced — it will never reach QuickBooks unless someone invoices it.' },
  delivery_problem: { label: 'Delivery problem', help: 'The driver recorded a problem: short, damaged, refused or nobody there.' },
  no_delivery_record: { label: 'No delivery record', help: 'A route delivery past its day that nobody has recorded as delivered.' },
};
const ISSUE_LABELS = { none: 'Delivered fine', short: 'Short — something missing', damaged: 'Damaged', refused: 'Refused', nobody_there: 'Nobody there', other: 'Other problem' };
const BASIS_LABELS = { requested: 'the date the customer asked for', 'delivery-day': "the customer's delivery day", 'next-business-day': 'the next business day', unknown: 'unknown' };
const RED = '#A50B18';

function fmtDay(d) {
  if (!d) return '—';
  return new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}
function fmtWhen(iso) {
  return iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
}
const addDays = (day, n) => new Date(Date.parse(`${day}T12:00:00Z`) + n * 86400000).toISOString().slice(0, 10);
const money = (n) => '$' + Math.round(n || 0).toLocaleString('en-US');
const mapsUrl = (a) => `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(a)}`;
const telUrl = (p) => `tel:${String(p).replace(/[^\d+]/g, '')}`;
const waitingToGoOut = (o) => (o.stage === 'ordered' || o.stage === 'picking') && !o.closed;

// Which rep's customers to show. A rep is keyed by email (by name for an old
// HubSpot owner with no email); 'all' and 'none' (no rep) are the other picks.
const repKey = (r) => (r.email || r.name || '').toLowerCase();
function forRep(board, rep) {
  if (!board || rep === 'all') return board;
  const keep = (o) => (rep === 'none' ? !o.rep : !!o.rep && repKey(o.rep) === rep);
  return { ...board, orders: board.orders.filter(keep), olderClosedNoInvoice: board.olderClosedNoInvoice.filter(keep) };
}

function RepFilter({ value, onChange, reps, me }) {
  const mine = me && me.email.toLowerCase();
  // A rep with nothing on the board still gets "My customers" to come back to.
  const options = me && me.role === 'sales_rep' && !reps.some((r) => repKey(r) === mine)
    ? [{ email: mine, name: me.name, orders: 0 }, ...reps] : reps;
  return (
    <label style={s.repBar} className="no-print">
      <span style={s.repLabel}>Customers</span>
      <select style={s.repSelect} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="all">All customers</option>
        {options.map((r) => (
          <option key={repKey(r)} value={repKey(r)}>{repKey(r) === mine ? 'My customers' : `${r.name}'s customers`} ({r.orders})</option>
        ))}
        <option value="none">No rep assigned</option>
      </select>
    </label>
  );
}

function Pill({ meta, title }) {
  return meta ? <span title={title} style={{ ...s.pill, color: meta.color, background: meta.bg }}>{meta.label}</span> : null;
}

function StageBar({ stage, method }) {
  const at = STAGE_ORDER.indexOf(stage);
  return (
    <div style={s.stageBar} aria-label={`Stage: ${STAGE_LABELS[stage]}`}>
      {STAGE_ORDER.map((st, i) => (
        <span key={st} style={{ ...s.stageStep, ...(i <= at ? s.stageDone : {}), ...(i === at ? s.stageNow : {}) }}>
          {st === 'delivered' && method === 'pickup' ? 'Picked up' : STAGE_LABELS[st]}
        </span>
      ))}
    </div>
  );
}

function ProblemPills({ problems }) {
  return (problems || []).map((p) => <span key={p} style={s.problemPill} title={PROBLEM_META[p]?.help}>{PROBLEM_META[p]?.label || p}</span>);
}

function OrderRow({ order, onOpen, showDay = true }) {
  return (
    <div style={s.card} role="button" tabIndex={0} onClick={() => onOpen(order.orderId)} onKeyDown={(e) => e.key === 'Enter' && onOpen(order.orderId)}>
      <div style={s.cardTop}>
        <span style={s.orderNo}>{order.number}</span>
        <Pill meta={METHOD_META[order.method]} />
        <ProblemPills problems={order.problems} />
        {showDay && <span style={s.when} title={`Expected day from ${BASIS_LABELS[order.expectedBasis]}`}>{fmtDay(order.expectedDay)}</span>}
      </div>
      <div style={s.cardTitle}>{order.customerName}</div>
      <StageBar stage={order.stage} method={order.method} />
      <div style={s.cardFoot}>
        <span>{order.totals.lines} line{order.totals.lines === 1 ? '' : 's'} · {order.totals.qty} units</span>
        <span>{money(order.total)}</span>
        {order.poNumber && <span>PO {order.poNumber}</span>}
        {order.rep && <span>{order.rep.name}</span>}
        {order.delivery && <span style={{ color: order.delivery.status === 'delivered' ? '#1a7f37' : '#b3261e' }}>{order.delivery.status === 'delivered' ? `Delivered to ${order.delivery.receivedBy}` : ISSUE_LABELS[order.delivery.issue]}</span>}
      </div>
    </div>
  );
}

function MethodChips({ value, onChange, orders }) {
  const count = (m) => orders.filter((o) => m === 'all' || o.method === m).length;
  return (
    <div style={s.chips}>
      {[['all', 'All'], ['route', 'Route'], ['fedex', 'FedEx'], ['pickup', 'Pickup']].map(([k, label]) => (
        <button key={k} style={value === k ? { ...s.chip, ...s.chipOn } : s.chip} onClick={() => onChange(k)}>{label} ({count(k)})</button>
      ))}
    </div>
  );
}

// ── Board: what has to go out, by day ──
function Board({ board, onOpen }) {
  const [method, setMethod] = useState('all');
  const today = board.today;
  const open = board.orders.filter(waitingToGoOut);
  const filtered = open.filter((o) => method === 'all' || o.method === method);
  const groups = [
    ['Overdue', filtered.filter((o) => o.expectedDay && o.expectedDay < today)],
    ['Today', filtered.filter((o) => o.expectedDay === today)],
    ['Tomorrow', filtered.filter((o) => o.expectedDay === addDays(today, 1))],
    ['Later', filtered.filter((o) => !o.expectedDay || o.expectedDay > addDays(today, 1))],
  ];
  const toConfirm = board.orders.filter((o) => o.method === 'route' && (o.stage === 'shipped' || o.stage === 'invoiced')
    && !o.delivery && o.expectedDay && o.expectedDay >= addDays(today, -2) && o.expectedDay <= today);
  return (
    <div>
      <h2 style={s.h2}>Waiting to go out</h2>
      <p style={s.muted}>Open orders not shipped yet, by the day they're expected out. Read live from SOS{board.fetchedAt ? ` at ${fmtWhen(board.fetchedAt)}` : ''}.</p>
      <MethodChips value={method} onChange={setMethod} orders={open} />
      {filtered.length === 0 && <div style={s.empty}>Nothing waiting to go out.</div>}
      {groups.map(([title, list]) => list.length > 0 && (
        <section key={title} style={s.section}>
          <h3 style={{ ...s.h3, color: title === 'Overdue' ? '#b3261e' : '#333' }}>{title} <span style={s.count}>{list.length}</span></h3>
          {list.sort((a, b) => String(a.expectedDay).localeCompare(String(b.expectedDay)) || a.customerName.localeCompare(b.customerName))
            .map((o) => <OrderRow key={o.orderId} order={o} onOpen={onOpen} showDay={title !== 'Today' && title !== 'Tomorrow'} />)}
        </section>
      ))}
      {toConfirm.length > 0 && (
        <section style={s.section}>
          <h3 style={s.h3}>Out on the route — not confirmed yet <span style={s.count}>{toConfirm.length}</span></h3>
          <p style={s.muted}>Shipped for delivery today or in the last two days, with no delivery recorded.</p>
          {toConfirm.map((o) => <OrderRow key={o.orderId} order={o} onOpen={onOpen} />)}
        </section>
      )}
    </div>
  );
}

// ── Deliveries: the driver's stops for a day ──
function Deliveries({ board, onOpen }) {
  const [day, setDay] = useState(board.today);
  const isToday = day === board.today;
  const stops = board.orders
    .filter((o) => o.method === 'route' && !(o.closed && o.stage !== 'invoiced' && o.stage !== 'delivered'))
    .filter((o) => o.expectedDay === day || (isToday && o.expectedDay < day && !o.delivery && o.stage !== 'delivered' && o.expectedDay >= addDays(day, -7)))
    .sort((a, b) => a.customerName.localeCompare(b.customerName));
  const done = stops.filter((o) => o.delivery).length;
  return (
    <div>
      <div style={s.titleRow} className="no-print">
        <h2 style={s.h2}>Route deliveries</h2>
        <button style={s.outlineSm} onClick={() => window.print()} disabled={!stops.length}>Print run sheet</button>
      </div>
      <div style={s.chips} className="no-print">
        {[[board.today, 'Today'], [addDays(board.today, 1), 'Tomorrow']].map(([d, label]) => (
          <button key={d} style={day === d ? { ...s.chip, ...s.chipOn } : s.chip} onClick={() => setDay(d)}>{label}</button>
        ))}
        <input type="date" style={s.input} value={day} onChange={(e) => e.target.value && setDay(e.target.value)} aria-label="Delivery day" />
      </div>
      <p style={s.muted}>{fmtDay(day)} · {stops.length} stop{stops.length === 1 ? '' : 's'}{stops.length ? ` · ${done} recorded` : ''}{isToday ? ' · includes earlier stops nobody recorded yet' : ''}</p>
      {stops.length === 0 && <div style={s.empty}>No route deliveries for this day.</div>}

      <div className="no-print">
        {stops.map((o, i) => (
          <div key={o.orderId} style={{ ...s.card, cursor: 'default' }}>
            <div style={s.cardTop}>
              <span style={s.stopNo}>{i + 1}</span>
              <span style={s.orderNo}>{o.number}</span>
              <Pill meta={{ label: STAGE_LABELS[o.stage], color: o.stage === 'delivered' ? '#1a7f37' : '#555', bg: o.stage === 'delivered' ? '#e6f4ea' : '#f0f0f0' }} />
              <ProblemPills problems={o.problems.filter((p) => p !== 'no_delivery_record')} />
              {o.expectedDay !== day && <span style={{ ...s.when, color: '#b3261e' }}>Was due {fmtDay(o.expectedDay)}</span>}
            </div>
            <div style={s.cardTitle}>{o.customerName}</div>
            {o.address && <div style={s.cardLine}>{o.address}</div>}
            {(o.customerNotes || o.deliveryNotes) && <div style={s.noteLine}>{[o.deliveryNotes, o.customerNotes].filter(Boolean).join(' · ')}</div>}
            <div style={s.cardFoot}>
              <span>{o.totals.lines} line{o.totals.lines === 1 ? '' : 's'} · {o.totals.qty} units</span>
              {o.poNumber && <span>PO {o.poNumber}</span>}
            </div>
            <div style={s.actionRow}>
              {o.address && <a style={s.contactBtn} href={mapsUrl(o.address)} target="_blank" rel="noreferrer">Directions</a>}
              {o.phone && <a style={s.contactBtn} href={telUrl(o.phone)}>Call {o.contact || ''}</a>}
              {o.delivery
                ? <button style={s.outlineSm} onClick={() => onOpen(o.orderId)}>{o.delivery.status === 'delivered' ? `✓ Delivered to ${o.delivery.receivedBy}` : `⚠ ${ISSUE_LABELS[o.delivery.issue]}`}</button>
                : <button style={s.primarySm} onClick={() => onOpen(o.orderId)}>Record delivery</button>}
            </div>
          </div>
        ))}
      </div>

      {/* Run sheet — only on paper */}
      <div className="print-only">
        <h2 style={{ margin: '0 0 4px' }}>Run sheet — {fmtDay(day)}</h2>
        <table style={s.printTable}>
          <thead><tr><th style={s.pth}>#</th><th style={s.pth}>Customer / address</th><th style={s.pth}>Contact</th><th style={s.pth}>Order</th><th style={s.pth}>Notes</th><th style={s.pth}>Received by</th></tr></thead>
          <tbody>{stops.map((o, i) => (
            <tr key={o.orderId}>
              <td style={s.ptd}>{i + 1}</td>
              <td style={s.ptd}><strong>{o.customerName}</strong><br />{o.address}</td>
              <td style={s.ptd}>{o.contact}<br />{o.phone}</td>
              <td style={s.ptd}>{o.number}<br />{o.totals.lines} lines · {o.totals.qty} units{o.poNumber ? <><br />PO {o.poNumber}</> : null}</td>
              <td style={s.ptd}>{[o.deliveryNotes, o.customerNotes].filter(Boolean).join(' · ')}</td>
              <td style={{ ...s.ptd, width: 140 }} />
            </tr>
          ))}</tbody>
        </table>
      </div>
    </div>
  );
}

// ── Problems ──
function Problems({ board, onOpen }) {
  const order = ['late', 'delivery_problem', 'no_delivery_record', 'backordered', 'not_invoiced', 'closed_no_invoice'];
  const groups = order.map((code) => [code, board.orders.filter((o) => o.problems.includes(code))]);
  const older = board.olderClosedNoInvoice || [];
  const total = groups.reduce((t, [, l]) => t + l.length, 0) + older.length;
  return (
    <div>
      <h2 style={s.h2}>Problems</h2>
      <p style={s.muted}>Orders that need someone to look at them. Tap one for the details.</p>
      {total === 0 && <div style={s.empty}>Nothing to chase right now.</div>}
      {groups.map(([code, list]) => list.length > 0 && (
        <section key={code} style={s.section}>
          <h3 style={s.h3}>{PROBLEM_META[code].label} <span style={s.count}>{list.length}</span></h3>
          <p style={s.muted}>{PROBLEM_META[code].help}</p>
          {list.map((o) => <OrderRow key={o.orderId} order={o} onOpen={onOpen} />)}
        </section>
      ))}
      {older.length > 0 && (
        <section style={s.section}>
          <h3 style={s.h3}>Older orders closed without an invoice <span style={s.count}>{older.length}</span></h3>
          <p style={s.muted}>From the last four months. Closed in SOS but never invoiced, so QuickBooks never billed them — {money(older.reduce((t, o) => t + o.total, 0))} in total. Check each one in SOS.</p>
          <div style={s.tableScroll}>
            <table style={s.table}>
              <thead><tr><th style={s.th}>Order</th><th style={s.th}>Date</th><th style={s.th}>Customer</th><th style={s.thR}>Total</th></tr></thead>
              <tbody>{older.sort((a, b) => b.orderDay.localeCompare(a.orderDay)).map((o) => (
                <tr key={o.orderId} style={{ cursor: 'pointer' }} onClick={() => onOpen(o.orderId)}>
                  <td style={s.td}>{o.number}</td><td style={s.td}>{o.orderDay}</td><td style={s.td}>{o.customerName}</td><td style={s.tdR}>{money(o.total)}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

// ── Find an order ──
function Find({ board, onOpen }) {
  const [q, setQ] = useState('');
  const all = useMemo(() => [...board.orders, ...(board.olderClosedNoInvoice || [])], [board]);
  const needle = q.trim().toLowerCase();
  const hits = needle ? all.filter((o) => [o.number, o.customerName, o.poNumber].some((v) => String(v || '').toLowerCase().includes(needle))).slice(0, 50) : [];
  return (
    <div>
      <h2 style={s.h2}>Find an order</h2>
      <input style={{ ...s.input, width: '100%', margin: '8px 0 12px' }} autoFocus placeholder="SO number, customer or PO…" value={q} onChange={(e) => setQ(e.target.value)} />
      <p style={s.muted}>Searches the last {board.windowDays} days of orders.</p>
      {needle && hits.length === 0 && <div style={s.empty}>No orders match.</div>}
      {hits.map((o) => <OrderRow key={o.orderId} order={o} onOpen={onOpen} />)}
    </div>
  );
}

// ── One order ──
function OrderDetail({ orderId, onBack, viewOnly }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ receivedBy: '', issue: 'none', note: '' });
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState('');

  const take = useCallback((res) => {
    setD(res);
    if (res.confirmation) setForm({ receivedBy: res.confirmation.receivedBy || '', issue: res.confirmation.issue || 'none', note: res.confirmation.note || '' });
  }, []);
  useEffect(() => { fulfillmentApi.order(orderId).then(take).catch((e) => setError(e.message)); }, [orderId, take]);

  if (!d) return <div><button style={s.backBtn} onClick={onBack}>← Back</button>{error ? <p style={s.err}>{error}</p> : <p style={s.muted}>Loading order…</p>}</div>;
  const o = d.order;

  async function record(e) {
    e.preventDefault();
    setBusy(true); setError(''); setSaved('');
    try { take(await fulfillmentApi.recordDelivery(o.orderId, form)); setSaved('Saved'); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }
  async function addPhoto(file) {
    if (!file) return;
    setBusy(true); setError('');
    try { take(await fulfillmentApi.addDeliveryPhoto(o.orderId, await resizeToJpeg(await readFileAsDataUrl(file), 1600, 0.75))); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  return (
    <div>
      <button style={s.backBtn} onClick={onBack}>← Back</button>
      <div style={s.cardTop}>
        <span style={s.orderNo}>{o.number}</span>
        <Pill meta={METHOD_META[o.method]} />
        <ProblemPills problems={o.problems} />
      </div>
      <h2 style={{ ...s.h2, marginTop: 6 }}>{o.customerName}</h2>
      <StageBar stage={o.stage} method={o.method} />

      <div style={s.facts}>
        <Fact label="Ordered" value={fmtDay(o.orderDay)} />
        <Fact label="Expected out" value={fmtDay(o.expectedDay)} hint={`from ${BASIS_LABELS[o.expectedBasis]}`} />
        <Fact label="Total" value={money(o.total)} />
        {o.poNumber && <Fact label="PO" value={o.poNumber} />}
        <Fact label="Rep" value={o.rep ? o.rep.name : '—'} />
        <Fact label="Pick ticket" value={o.pickTickets.join(', ') || '—'} />
        <Fact label="Shipment" value={o.shipments.join(', ') || '—'} />
        <Fact label="Invoice" value={o.invoices.join(', ') || '—'} />
      </div>

      {(o.address || o.phone) && (
        <div style={s.actionRow}>
          {o.address && <a style={s.contactBtn} href={mapsUrl(o.address)} target="_blank" rel="noreferrer">Directions</a>}
          {o.phone && <a style={s.contactBtn} href={telUrl(o.phone)}>Call {o.contact || ''}</a>}
        </div>
      )}
      {o.address && <div style={s.cardLine}>{o.address}</div>}
      {(o.customerNotes || o.deliveryNotes) && <div style={s.noteLine}>{[o.deliveryNotes, o.customerNotes].filter(Boolean).join(' · ')}</div>}

      <section style={s.section}>
        <h3 style={s.h3}>Lines</h3>
        <div style={s.tableScroll}>
          <table style={s.table}>
            <thead><tr><th style={s.th}>Item</th><th style={s.thR}>Qty</th><th style={s.thR}>Picked</th><th style={s.thR}>Shipped</th><th style={s.thR}>Invoiced</th><th style={s.thR}>Backordered</th></tr></thead>
            <tbody>{d.lines.map((l, i) => (
              <tr key={i}>
                <td style={s.td}>{l.item.split(':').pop()}<div style={s.mutedSm}>{l.description}</div></td>
                <td style={s.tdR}>{l.quantity}{l.uom ? ` ${l.uom}` : ''}</td>
                <td style={s.tdR}>{l.picked || ''}</td>
                <td style={s.tdR}>{l.shipped || ''}</td>
                <td style={s.tdR}>{l.invoiced || ''}</td>
                <td style={{ ...s.tdR, color: l.backOrdered ? '#b3261e' : undefined }}>{l.backOrdered || ''}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </section>

      <section style={s.section}>
        <h3 style={s.h3}>{o.method === 'pickup' ? 'Pickup' : 'Delivery'}</h3>
        {d.confirmation && (
          <p style={{ ...s.muted, color: d.confirmation.status === 'delivered' ? '#1a7f37' : '#b3261e' }}>
            {d.confirmation.status === 'delivered' ? `Delivered to ${d.confirmation.receivedBy}` : ISSUE_LABELS[d.confirmation.issue]} — recorded by {d.confirmation.confirmedByName || d.confirmation.confirmedBy} {fmtWhen(d.confirmation.confirmedAt)}
          </p>
        )}
        {viewOnly ? (!d.confirmation && <p style={s.muted}>Nothing recorded yet. Drivers and the office record deliveries here.</p>) : (
        <form onSubmit={record} style={s.formGrid}>
          <label style={s.field}><span style={s.fieldLabel}>How did it go?</span>
            <select style={s.input} value={form.issue} onChange={(e) => setForm((f) => ({ ...f, issue: e.target.value }))}>
              {Object.entries(ISSUE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          <label style={s.field}><span style={s.fieldLabel}>Received by{form.issue === 'none' ? '' : ' (if anyone)'}</span>
            <input style={s.input} value={form.receivedBy} onChange={(e) => setForm((f) => ({ ...f, receivedBy: e.target.value }))} placeholder="Name of who took it" />
          </label>
          <label style={s.field}><span style={s.fieldLabel}>Note{form.issue === 'none' ? ' (optional)' : ' — what went wrong'}</span>
            <textarea style={s.textarea} rows={2} value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder="e.g. left at the back door, one case short" />
          </label>
          <div style={s.actionRow}>
            <button style={s.primary} type="submit" disabled={busy}>{busy ? 'Saving…' : d.confirmation ? 'Update' : `Record ${o.method === 'pickup' ? 'pickup' : 'delivery'}`}</button>
            {saved && <span style={s.savedNote}>{saved}</span>}
          </div>
        </form>
        )}
        {error && <p style={s.err}>{error}</p>}
        {d.confirmation && (
          <div style={s.photoGrid}>
            {(d.confirmation.photos || []).map((p) => <a key={p.key} href={p.url} target="_blank" rel="noreferrer"><img src={p.url} alt={`Taken ${fmtWhen(p.at)}`} style={s.photo} /></a>)}
            {!viewOnly && (
              <label style={s.photoAdd}>
                {busy ? 'Uploading…' : '+ Add photo'}
                <input type="file" accept="image/*" style={{ display: 'none' }} disabled={busy} onChange={(e) => { addPhoto(e.target.files && e.target.files[0]); e.target.value = ''; }} />
              </label>
            )}
          </div>
        )}
      </section>
    </div>
  );
}

function Fact({ label, value, hint }) {
  return (
    <div style={s.fact}>
      <div style={s.factLabel}>{label}</div>
      <div style={s.factValue}>{value}</div>
      {hint && <div style={s.mutedSm}>{hint}</div>}
    </div>
  );
}

const PRINT_CSS = `
  .print-only { display: none; }
  @media print {
    .no-print { display: none !important; }
    .print-only { display: block !important; }
    body { background: #fff !important; }
  }
`;

const s = {

  page: { minHeight: '100vh', background: '#f5f5f5', color: '#333' },
  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '10px 16px', background: RED, color: '#fff', flexWrap: 'wrap' },
  logoImg: { height: 28, display: 'block' },
  brandSmSub: { marginLeft: 10, color: 'rgba(255,255,255,0.8)', textTransform: 'uppercase', letterSpacing: 2, fontSize: '.65rem', whiteSpace: 'nowrap' },
  topRight: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  who: { color: 'rgba(255,255,255,0.9)', fontSize: '.85rem' },
  linkBtn: { background: 'none', border: '1px solid rgba(255,255,255,0.5)', color: '#fff', cursor: 'pointer', fontSize: '.8rem', padding: '4px 10px', borderRadius: 6 },
  tabs: { display: 'flex', gap: 4, padding: '0 12px', background: '#fff', borderBottom: '1px solid #e5e5e5', overflowX: 'auto', alignItems: 'center' },
  tab: { padding: '12px 14px', border: 'none', background: 'none', fontSize: '.92rem', color: '#777', cursor: 'pointer', borderBottom: '3px solid transparent', whiteSpace: 'nowrap', fontWeight: 500 },
  tabActive: { color: RED, borderBottom: `3px solid ${RED}`, fontWeight: 600 },
  refresh: { marginLeft: 'auto', padding: '6px 10px', border: '1px solid #ddd', background: '#fff', borderRadius: 6, fontSize: '.8rem', color: '#555', cursor: 'pointer', whiteSpace: 'nowrap' },
  body: { maxWidth: 900, margin: '0 auto', padding: '16px 16px 48px' },

  titleRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, margin: '4px 0 8px' },
  h2: { margin: '4px 0', fontSize: '1.35rem', color: '#222' },
  h3: { margin: '0 0 6px', fontSize: '1.02rem', color: '#333' },
  count: { color: '#999', fontWeight: 500, fontSize: '.85rem', marginLeft: 4 },
  section: { margin: '20px 0' },
  muted: { color: '#888', fontSize: '.86rem', margin: '4px 0 10px' },
  mutedSm: { color: '#999', fontSize: '.76rem' },
  err: { color: '#c0392b', fontSize: '.9rem' },
  empty: { background: '#fff', border: '1px dashed #ddd', borderRadius: 10, padding: '20px 16px', color: '#777', textAlign: 'center', fontSize: '.92rem' },

  chips: { display: 'flex', gap: 8, flexWrap: 'wrap', margin: '8px 0 12px', alignItems: 'center' },
  repBar: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 },
  repLabel: { fontSize: '.8rem', fontWeight: 600, color: '#666', textTransform: 'uppercase', letterSpacing: '.04em' },
  repSelect: { padding: '7px 10px', border: '1px solid #ddd', borderRadius: 8, fontSize: '.9rem', background: '#fff', color: '#333', maxWidth: '100%' },
  chip: { padding: '7px 12px', background: '#fff', border: '1px solid #ddd', borderRadius: 999, cursor: 'pointer', fontSize: '.85rem', color: '#555' },
  chipOn: { border: `1px solid ${RED}`, background: '#fff0f0', color: RED, fontWeight: 600 },

  card: { background: '#fff', border: '1px solid #e3e3e3', borderRadius: 10, padding: '12px 14px', marginBottom: 10, cursor: 'pointer' },
  cardTop: { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  orderNo: { fontWeight: 700, color: '#444', fontSize: '.85rem', fontVariantNumeric: 'tabular-nums' },
  stopNo: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: '50%', background: RED, color: '#fff', fontSize: '.75rem', fontWeight: 700 },
  when: { marginLeft: 'auto', fontSize: '.8rem', color: '#777', whiteSpace: 'nowrap' },
  cardTitle: { fontWeight: 600, fontSize: '1rem', color: '#222', margin: '6px 0' },
  cardLine: { color: '#555', fontSize: '.88rem', margin: '2px 0' },
  noteLine: { color: '#8a5a00', background: '#fff8e6', borderRadius: 6, padding: '4px 8px', fontSize: '.84rem', margin: '6px 0' },
  cardFoot: { display: 'flex', gap: 12, flexWrap: 'wrap', color: '#888', fontSize: '.8rem', marginTop: 8 },
  pill: { display: 'inline-block', padding: '2px 9px', borderRadius: 999, fontSize: '.72rem', fontWeight: 600, whiteSpace: 'nowrap' },
  problemPill: { display: 'inline-block', padding: '2px 9px', borderRadius: 999, fontSize: '.72rem', fontWeight: 600, whiteSpace: 'nowrap', color: '#b3261e', background: '#fdecea', cursor: 'help' },

  stageBar: { display: 'flex', gap: 3, margin: '4px 0', flexWrap: 'wrap' },
  stageStep: { flex: '1 1 60px', textAlign: 'center', fontSize: '.7rem', padding: '3px 2px', borderRadius: 4, background: '#f0f0f0', color: '#aaa', whiteSpace: 'nowrap' },
  stageDone: { background: '#e6f4ea', color: '#1a7f37' },
  stageNow: { background: '#1a7f37', color: '#fff', fontWeight: 600 },

  actionRow: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', margin: '10px 0 4px' },
  contactBtn: { padding: '10px 14px', background: '#fff', border: '1px solid #ddd', borderRadius: 8, textDecoration: 'none', color: '#1a5fb4', fontWeight: 600, fontSize: '.9rem' },
  primary: { padding: '12px 18px', background: RED, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: '.95rem' },
  primarySm: { padding: '10px 14px', background: RED, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: '.9rem' },
  outlineSm: { padding: '10px 14px', background: '#fff', border: '1px solid #ddd', borderRadius: 8, cursor: 'pointer', color: '#333', fontSize: '.88rem' },
  backBtn: { background: 'none', border: 'none', color: RED, cursor: 'pointer', fontSize: '.92rem', padding: '4px 0', marginBottom: 8 },
  savedNote: { color: '#1a7f37', fontSize: '.88rem' },

  facts: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8, margin: '12px 0' },
  fact: { background: '#fff', border: '1px solid #ececec', borderRadius: 8, padding: '8px 10px' },
  factLabel: { color: '#888', fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: 0.5 },
  factValue: { fontWeight: 600, fontSize: '.92rem', color: '#333', overflowWrap: 'anywhere' },

  input: { padding: '10px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: '1rem', background: '#fff', color: '#333', boxSizing: 'border-box', minWidth: 0 },
  textarea: { padding: '10px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: '1rem', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box', resize: 'vertical' },
  formGrid: { display: 'flex', flexDirection: 'column', gap: 10, margin: '8px 0' },
  field: { display: 'flex', flexDirection: 'column', gap: 4 },
  fieldLabel: { fontSize: '.8rem', fontWeight: 600, color: '#555' },

  tableScroll: { width: '100%', overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10 },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: '1px solid #ececec', color: '#777', fontSize: '.74rem', textTransform: 'uppercase', letterSpacing: 0.5 },
  thR: { textAlign: 'right', padding: '8px 10px', borderBottom: '1px solid #ececec', color: '#777', fontSize: '.74rem', textTransform: 'uppercase', letterSpacing: 0.5 },
  td: { padding: '8px 10px', borderBottom: '1px solid #f3f3f3', fontSize: '.88rem' },
  tdR: { textAlign: 'right', padding: '8px 10px', borderBottom: '1px solid #f3f3f3', fontSize: '.88rem', fontVariantNumeric: 'tabular-nums' },

  photoGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: 8, marginTop: 10 },
  photo: { width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', borderRadius: 8, border: '1px solid #e3e3e3', display: 'block' },
  photoAdd: { display: 'flex', alignItems: 'center', justifyContent: 'center', aspectRatio: '1 / 1', border: '1px dashed #bbb', borderRadius: 8, color: RED, fontWeight: 600, fontSize: '.85rem', cursor: 'pointer', background: '#fff', textAlign: 'center', padding: 6 },

  printTable: { width: '100%', borderCollapse: 'collapse', fontSize: '11pt' },
  pth: { textAlign: 'left', borderBottom: '2px solid #000', padding: '4px 6px' },
  ptd: { borderBottom: '1px solid #999', padding: '6px', verticalAlign: 'top' },
};
